import { Divider, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import VetsiIcon from 'src/assets/icons/VetsiIcon';
import { translateKey } from 'src/locales/translate';
import { GetProfileMedicationsDto } from 'woofwoof-api';
import MedicationSummaryStock from './MedicationSummaryStock';
import { SPAN_FULL } from './medicationFormLayout';
import {
  formatDosing,
  formatDuration,
  formatStatus,
  formatStopReason,
  formatStrength,
} from './medicationDisplay';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';

interface MedicationFormSummaryProps {
  hideLifecycle?: boolean;
  hideReminder?: boolean;
  /** A medication being added has no status yet — the start date decides it. */
  showStatus?: boolean;
  creatorName?: string;
  creatorOrganizationName?: string;
  /** Facility screens (#692): what the save takes from stock, or gives back. */
  showStock?: boolean;
}

/**
 * The last step: everything the vet just filled in, on one screen, before it is
 * written. Reads through the same formatters as the patient table, so the
 * summary and the row that appears afterwards cannot disagree.
 */
export default function MedicationFormSummary({
  hideLifecycle = false,
  hideReminder = false,
  showStatus = false,
  creatorName,
  creatorOrganizationName,
  showStock = false,
}: MedicationFormSummaryProps) {
  const { watch } = useFormContext<MedicationsFormModalValuesProps>();
  const values = watch();
  const preview = toPreviewDto(values);

  return (
    <Stack sx={{ ...SPAN_FULL, gap: 2 }} data-testid="medication-summary">
      <Section title={translateKey('medication.form.stepMedicine')}>
        <Row
          label={translateKey('medication.form.summaryName')}
          value={values.name}
        />
        <Row
          label={translateKey('medication.form.summaryPreparation')}
          value={values.preparationType}
        />
        <Row
          label={translateKey('medication.table.addedBy')}
          value={[creatorName, creatorOrganizationName]
            .filter(Boolean)
            .join(' · ')}
        />
      </Section>

      <Divider />

      <Section title={translateKey('medication.form.stepDosage')}>
        <Row
          label={translateKey('medication.table.strength')}
          value={formatStrength(preview)}
        />
        <Row
          label={translateKey('medication.table.dosing')}
          value={formatDosing(preview)}
        />
        <Row
          label={translateKey('medication.table.duration')}
          value={formatDuration(preview)}
        />
        <Row
          label={translateKey('medication.form.dosageSchedule')}
          value={values.dosageSchedule}
        />
        <Row label="Notatki" value={values.notes} />
        {showStock && <MedicationSummaryStock />}
      </Section>

      {!hideLifecycle && (
        <>
          <Divider />
          <Section title={translateKey('medication.form.stepCourse')}>
            <Row
              label={translateKey('medication.form.indication')}
              value={values.indication}
            />
            <Row
              label={translateKey('medication.form.startedAt')}
              value={formatDay(values.startedAt)}
            />
            <Row
              label={translateKey('medication.form.adverseReaction')}
              value={
                values.adverseReaction
                  ? values.adverseReactionNote ||
                    translateKey('medication.form.summaryYes')
                  : undefined
              }
            />
          </Section>
        </>
      )}

      {showStatus && (
        <>
          <Divider />
          <Section title={translateKey('medication.form.stepStatus')}>
            <Row
              label={translateKey('medication.form.status')}
              value={formatStatus(preview)}
            />
            <Row
              label={translateKey('medication.form.stopReason')}
              value={formatStopReason(preview)}
            />
            <Row
              label={translateKey('medication.form.endedAt')}
              value={formatDay(values.endedAt)}
            />
          </Section>
        </>
      )}

      {!hideReminder && (
        <>
          <Divider />
          <Section title={translateKey('visit.visitCourseMenuReminder')}>
            {values.reminderEnabled ? (
              <>
                <Row
                  label={translateKey('medication.form.summaryReminderWhen')}
                  value={formatDayTime(values.reminderScheduledAt)}
                />
                <Row
                  label={translateKey('medication.form.summaryReminderRepeat')}
                  value={values.reminderRecurrenceType}
                />
                <Row
                  label={translateKey('medication.form.summaryReminderChannels')}
                  value={[
                    values.reminderEmailEnabled
                      ? translateKey('search.book.cardEmail')
                      : undefined,
                    values.reminderSmsEnabled ? 'SMS' : undefined,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {translateKey('medication.form.summaryNoReminder')}
              </Typography>
            )}
          </Section>
        </>
      )}
    </Stack>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Stack gap={1}>
      <Stack direction="row" gap={1} alignItems="center">
        <VetsiIcon />
        <Typography variant="body1">{title}</Typography>
      </Stack>
      {children}
    </Stack>
  );
}

/** A row is only worth a line when it has something to say. */
function Row({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {`${label}:`}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

const asNumber = (value: number | '') =>
  value === '' ? undefined : Number(value);

/**
 * The form's values in the shape the display helpers read, so the summary
 * renders a medication exactly as the table will once it is saved.
 */
function toPreviewDto(
  values: MedicationsFormModalValuesProps,
): GetProfileMedicationsDto {
  return {
    id: '',
    dosagePending: values.dosagePending,
    adverseReaction: values.adverseReaction,
    status: values.status,
    strengthValue: asNumber(values.strengthValue),
    strengthUnit: values.strengthUnit || undefined,
    strengthUnitCustom: values.strengthUnitCustom || undefined,
    form: values.form || undefined,
    formCustom: values.formCustom || undefined,
    quantityValue: asNumber(values.quantityValue),
    quantityCustom: values.quantityCustom || undefined,
    frequencyType: values.frequencyType || undefined,
    frequencyValue: asNumber(values.frequencyValue),
    frequencyCustom: values.frequencyCustom || undefined,
    durationType: values.durationType || undefined,
    durationValue: asNumber(values.durationValue),
    durationCustom: values.durationCustom || undefined,
    dosageSchedule: values.dosageSchedule,
    stopReason: values.stopReason || undefined,
    stopReasonNote: values.stopReasonNote,
  } as GetProfileMedicationsDto;
}

const formatDay = (value: Date | null) =>
  value ? format(new Date(value), 'd MMMM y', { locale: pl }) : undefined;

const formatDayTime = (value: Date | null) =>
  value ? format(new Date(value), 'd MMMM y, HH:mm', { locale: pl }) : undefined;
